import React, { useEffect, useState, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { FAQAccordion } from '../components/FAQAccordion';
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}
const faqs: FAQItem[] = [{
  category: 'General',
  question: 'What is a medico-legal report?',
  answer: 'A medico-legal report is an independent medical assessment prepared by a qualified expert for use in legal proceedings. It provides an objective opinion on medical issues relevant to a case, such as injury causation, prognosis, and treatment needs.'
}, {
  category: 'General',
  question: 'How do I instruct an expert?',
  answer: 'You can instruct an expert by contacting us via email at info@uk-medicolegal-assessment.com. Provide case details, medical records, and specific questions you need addressed. We will confirm the instruction and arrange the assessment.'
}, {
  category: 'General',
  question: 'Are your experts independent?',
  answer: 'Yes, all our experts are fully independent and comply with CPR Part 35 requirements. They owe their primary duty to the court, not to the instructing party, ensuring unbiased and objective opinions.'
}, {
  category: 'Services',
  question: 'What specialties do you cover?',
  answer: 'We have a comprehensive panel covering all major medical specialties including orthopaedics, psychiatry, neurology, ENT, general practice, pain management, and many more. If you need a specific specialty, please contact us to confirm availability.'
}, {
  category: 'Services',
  question: 'Do you provide expert witnesses for court?',
  answer: "Yes, our experts are available for court attendance, conferences, and cross-examination. Court attendance is charged separately and quoted in advance based on the expert's daily rate and preparation time required."
}, {
  category: 'Services',
  question: 'Can you provide urgent reports?',
  answer: 'Yes, we offer expedited services for urgent cases subject to expert availability. Urgent instructions typically incur a premium fee. Contact us to discuss your timescales and we will do our best to accommodate.'
}, {
  category: 'Process',
  question: 'How long does it take to get a report?',
  answer: 'Standard turnaround is typically 4-6 weeks from instruction to report delivery, depending on case complexity and expert availability. This includes appointment arrangement, examination, and report preparation. Urgent cases can be expedited.'
}, {
  category: 'Process',
  question: 'Where do appointments take place?',
  answer: "Appointments can be arranged at the expert's clinic, claimant's home, solicitor's office, or a neutral venue depending on case requirements and claimant mobility. We aim to minimize travel burden for all parties."
}, {
  category: 'Process',
  question: "What happens if the claimant doesn't attend?",
  answer: 'If a claimant fails to attend without reasonable notice, a DNA (Did Not Attend) charge of 100% of the appointment fee will apply. See our Cancellation Policy for full details on notice periods and charges.'
}, {
  category: 'Pricing',
  question: 'How much do reports cost?',
  answer: 'Fees vary depending on specialty, case complexity, and report type. We provide transparent quotes in advance. Contact us for a specific quote for your case.'
}, {
  category: 'Pricing',
  question: 'What payment terms do you offer?',
  answer: 'Standard payment terms are 30 days from invoice date. We can discuss alternative arrangements for regular clients or high-volume instructions. Payment can be made by bank transfer, cheque, or credit/debit card.'
}, {
  category: 'Pricing',
  question: 'Are there any additional charges?',
  answer: 'Additional charges may apply for supplementary reports, court attendance, conferences, extensive medical record review, or urgent instructions. All additional charges are quoted in advance and agreed before proceeding.'
}, {
  category: 'Legal',
  question: 'Are your reports CPR Part 35 compliant?',
  answer: 'Yes, all our reports are prepared in accordance with Civil Procedure Rules Part 35 and include the required expert declaration, statement of truth, and compliance with Practice Direction 35.'
}, {
  category: 'Legal',
  question: 'Do you have professional indemnity insurance?',
  answer: 'Yes, we maintain comprehensive professional indemnity insurance covering all our services and expert panel. Details and certificates are available on request.'
}, {
  category: 'Legal',
  question: 'How do you ensure data protection compliance?',
  answer: 'We are fully GDPR compliant with robust data security measures including encrypted storage, secure transmission, access controls, and staff training. See our Privacy Policy for full details of our data protection practices.'
}];
const categories = ['All', 'General', 'Services', 'Process', 'Pricing', 'Legal'];
export function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredFaqs = activeCategory === 'All' ? faqs : faqs.filter(faq => faq.category === activeCategory);
  useEffect(() => {
    document.title = 'Frequently Asked Questions | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Answers to common questions about medico-legal reports, expert witness services, turnaround times, fees, and the instruction process. UK Medicolegal Assessment.');
    setMeta('robots', 'index, follow');
  }, []);
  return <div className="min-h-screen bg-warm-50" data-id="element-858">
      <Navigation data-id="element-859" />

      <div className="pt-32 pb-20 md:pt-40 md:pb-24" data-id="element-860">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-861">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-12" data-id="element-862">
            <div className="bg-teal-50 p-4 rounded-2xl flex-shrink-0" data-id="element-863">
              <HelpCircle className="h-8 w-8 text-teal-600" data-id="element-864" />
            </div>
            <div data-id="element-865">
              <h1 className="text-4xl md:text-5xl font-bold text-ink-900 tracking-tight" data-id="element-866">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-ink-500 mt-3 leading-relaxed" data-id="element-867">
                Find answers to common questions about our services
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-10" data-id="element-868">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${activeCategory === category ? 'bg-cta-500 text-white shadow-sm' : 'bg-white border border-warm-200 text-ink-700 hover:border-teal-300 hover:bg-warm-50'}`} data-id="element-869">
                {category}
              </button>)}
          </div>

          {/* FAQ Accordion */}
          <FAQAccordion items={filteredFaqs} data-id="element-870" />

          {/* Still have questions CTA */}
          <div className="mt-16 bg-cta-500 rounded-[2rem] p-10 md:p-12 text-center shadow-lg" data-id="element-871">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight" data-id="element-872">
              Still have questions?
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-873">
              Our team is here to help. Get in touch and we'll respond within 2
              hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-874">
              <a href="mailto:info@uk-medicolegal-assessment.com?subject=New%20Website%20Enquiry" className="inline-flex items-center justify-center bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white rounded-xl py-3.5 px-7 font-semibold shadow-sm transition-colors duration-200 text-base min-h-[48px]" data-id="element-875">
                Email Us
              </a>
              <a href="/contact" className="inline-flex items-center justify-center bg-white border-2 border-transparent hover:bg-warm-50 text-ink-900 rounded-xl py-3.5 px-7 font-semibold transition-colors duration-200 text-base min-h-[48px]" data-id="element-876">
                Contact Page
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer data-id="element-877" />
    </div>;
}